import React, { useState, useEffect, useCallback } from 'react';
import { Presentation, RefreshCw, ExternalLink, Check, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const getToken = () => localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');

/**
 * BoardDeckCard — monthly board-deck generator.
 * • Shows the latest deck's cover headline + month + status
 * • Regenerate button (calls Claude Sonnet 4.5)
 * • Open rendered HTML deck in a new tab (print to PDF from there)
 * • Approve (marks status=approved; metadata only — worker doesn't auto-mail it, unlike the weekly pulse)
 */
const BoardDeckCard = () => {
  const [deck, setDeck] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);

  const load = useCallback(async () => {
    const t = getToken();
    if (!t) { setLoading(false); return; }
    try {
      const r = await fetch(`${API}/api/admin/board-deck/current`, { headers: { 'Authorization': `Bearer ${t}` } });
      if (r.status === 403) { setError('not_master'); setLoading(false); return; }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setDeck(d.deck);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (deck && !window.confirm('Regenerate this month\'s board deck? The current draft will be overwritten.')) return;
    setBusy(true); setFlash(null);
    try {
      const r = await fetch(`${API}/api/admin/board-deck/generate`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const d = await r.json();
      setDeck(d.deck);
      setFlash({ ok: 'New deck drafted via Claude Sonnet 4.5. Open to review.' });
    } catch (e) {
      setFlash({ err: e.message });
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    if (!deck) return;
    setBusy(true); setFlash(null);
    try {
      const r = await fetch(`${API}/api/admin/board-deck/save`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ deck_id: deck.deck_id, payload: deck.payload, status: 'approved' }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setDeck(d.deck);
      setFlash({ ok: 'Deck approved.' });
    } catch (e) {
      setFlash({ err: e.message });
    } finally {
      setBusy(false);
    }
  };

  const openDeck = async () => {
    if (!deck) return;
    setBusy(true); setFlash(null);
    try {
      const r = await fetch(`${API}/api/admin/board-deck/${deck.deck_id}/html`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const html = await r.text();
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke after 60s to free memory (tab has already loaded by then)
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setFlash({ err: `Could not open deck: ${e.message}` });
    } finally {
      setBusy(false);
    }
  };

  if (error === 'not_master') return null;
  if (loading) return <div className="bg-white rounded-lg border p-4 animate-pulse" data-testid="board-deck-loading" style={{ borderColor: '#E0E0E0', minHeight: '140px' }} />;

  const isApproved = deck?.status === 'approved';

  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid="board-deck-card" style={{ borderColor: '#E0E0E0' }}>
      <div className="px-5 py-4 border-b flex items-center justify-between flex-wrap gap-2" style={{ borderColor: '#F0F0F0', background: 'linear-gradient(135deg, rgba(123,79,216,0.06), rgba(245,131,31,0.04))' }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#7B4FD8', color: 'white' }}>
            <Presentation size={18} />
          </div>
          <div>
            <h3 className="text-base font-black" style={{ color: '#1A1209' }}>Monthly Board Deck</h3>
            <p className="text-[11px] text-gray-500">
              6-slide deck auto-drafted via Claude Sonnet 4.5
              {deck && <> · <b>{deck.month}</b> · <span className={isApproved ? 'text-green-700' : 'text-purple-700'}>{isApproved ? 'Approved' : 'Draft'}</span></>}
            </p>
          </div>
        </div>
        <button onClick={generate} disabled={busy}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors hover:bg-purple-50 disabled:opacity-50"
                style={{ borderColor: '#7B4FD8', color: '#7B4FD8' }}
                data-testid="board-deck-generate">
          {busy ? <RefreshCw size={12} className="inline animate-spin mr-1" /> : <RefreshCw size={12} className="inline mr-1" />}
          {deck ? 'Regenerate deck' : 'Generate deck'}
        </button>
      </div>

      <div className="p-5">
        {flash && (
          <div className={`mb-3 px-3 py-2 rounded-lg text-xs font-semibold ${flash.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {flash.ok ? <><Check size={12} className="inline mr-1" />{flash.ok}</> : <><AlertCircle size={12} className="inline mr-1" />{flash.err}</>}
          </div>
        )}

        {!deck && (
          <div className="text-center py-6 text-sm text-gray-500" data-testid="board-deck-empty">
            No deck yet this month. Click <b>Generate deck</b> to have Claude read the last 4 weekly snapshots and draft a 6-slide deck.
          </div>
        )}

        {deck && (
          <>
            <div className="rounded-xl p-5 mb-4" style={{ backgroundColor: '#0b0d12', color: 'white' }}>
              <div className="text-[10px] uppercase tracking-widest font-bold mb-3" style={{ color: '#F5831F' }}>
                Cover · {deck.payload?.cover?.month_label}
              </div>
              <div className="text-4xl font-black mb-2" style={{ color: '#F5831F', letterSpacing: '-0.02em' }}>
                {deck.payload?.cover?.headline_metric}
              </div>
              <div className="text-lg" style={{ color: '#e8e9ed' }}>
                {deck.payload?.cover?.headline_narrative}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-2 mb-4 text-[11px]">
              {[
                { label: 'Revenue', title: deck.payload?.mrr_growth?.title },
                { label: 'Engagement', title: deck.payload?.engagement?.title },
                { label: 'Safety', title: deck.payload?.safety?.title },
                { label: 'Campaigns', title: deck.payload?.campaigns?.title },
                { label: 'Asks', title: deck.payload?.asks?.title },
              ].map((s, i) => (
                <div key={i} className="px-3 py-2 border rounded-lg text-center" style={{ borderColor: '#E0E0E0' }}>
                  <div className="text-gray-500 uppercase tracking-wider text-[9px] font-bold">{s.label}</div>
                  <div className="font-semibold text-gray-800 truncate mt-1">{s.title || '—'}</div>
                </div>
              ))}
            </div>

            <div className="flex items-center flex-wrap gap-2 justify-end">
              <button onClick={openDeck} disabled={busy}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg border hover:bg-gray-50 flex items-center gap-1 disabled:opacity-50"
                      style={{ borderColor: '#E0E0E0', color: '#333' }}
                      data-testid="board-deck-open">
                <ExternalLink size={12} />Open deck (print to PDF)
              </button>
              <button onClick={approve} disabled={busy}
                      className="text-xs font-bold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                      style={{ backgroundColor: isApproved ? '#2EAA5C' : '#7B4FD8', color: 'white' }}
                      data-testid="board-deck-approve">
                <Check size={12} className="inline mr-1" />
                {isApproved ? 'Approved' : 'Approve deck'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BoardDeckCard;
